import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import api from '../api/axios';

export interface Account {
  id: string;
  name: string;
}

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_superuser: boolean;
}

interface AuthContextType {
  access: string | null;
  user: User | null;
  accounts: Account[];
  currentAccountId: string | null;
  currentAccount: Account | null;
  isLoading: boolean;
  setTokens: (access: string, refresh: string) => void;
  setCurrentAccountId: (id: string) => void;
  refreshAccounts: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [access, setAccess] = useState<string | null>(localStorage.getItem('access'));
  const [user, setUser] = useState<User | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [currentAccountId, setCurrentAccountIdState] = useState<string | null>(
    localStorage.getItem('current_account_id')
  );
  const [isLoading, setIsLoading] = useState(true);

  const currentAccount = accounts.find(a => a.id === currentAccountId) || null;

  const logout = useCallback(() => {
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('current_account_id');
    setAccess(null);
    setUser(null);
    setAccounts([]);
    setCurrentAccountIdState(null);
    delete api.defaults.headers.common.Authorization;
    delete api.defaults.headers.common['X-Account-ID'];
    // Reset PostHog identity
    if (typeof window !== 'undefined' && (window as any).posthog?.reset) {
      (window as any).posthog.reset();
    }
  }, []);

  const setTokens = useCallback((accessToken: string, refreshToken: string) => {
    localStorage.setItem('access', accessToken);
    localStorage.setItem('refresh', refreshToken);
    setAccess(accessToken);
  }, []);

  const setCurrentAccountId = useCallback((id: string) => {
    localStorage.setItem('current_account_id', id);
    setCurrentAccountIdState(id);
    api.defaults.headers.common['X-Account-ID'] = id;
  }, []);

  const refreshAccounts = useCallback(async () => {
    try {
      const res = await api.get('/auth/me/');
      let allAccounts: Account[] = res.data.accounts || [];
      if (res.data.is_superuser) {
        try {
          const acctRes = await api.get('/accounts/');
          allAccounts = acctRes.data;
        } catch { /* fall back */ }
      }
      setAccounts(allAccounts);
    } catch { /* ignore */ }
  }, []);

  // Fetch user info when access token is set
  useEffect(() => {
    if (!access) {
      setUser(null);
      setAccounts([]);
      setIsLoading(false);
      return;
    }

    api.get('/auth/me/')
      .then(async (res) => {
        setUser(res.data);

        // Identify user in PostHog
        if (typeof window !== 'undefined' && (window as any).posthog?.identify) {
          (window as any).posthog.identify(res.data.id, {
            email: res.data.email,
            name: `${res.data.first_name} ${res.data.last_name}`.trim(),
          });
        }

        // Admins see all accounts; regular users see only their own
        let allAccounts: Account[] = res.data.accounts || [];
        if (res.data.is_superuser) {
          try {
            const acctRes = await api.get('/accounts/');
            allAccounts = acctRes.data;
          } catch {
            // Fall back to user's own accounts
          }
        }
        setAccounts(allAccounts);

        // Determine current account
        const storedAccountId = localStorage.getItem('current_account_id');
        const validStored = allAccounts.some((a: Account) => a.id === storedAccountId);

        if (validStored && storedAccountId) {
          setCurrentAccountIdState(storedAccountId);
          api.defaults.headers.common['X-Account-ID'] = storedAccountId;
        } else if (res.data.default_account_id) {
          setCurrentAccountId(res.data.default_account_id);
        }
      })
      .catch(() => {
        logout();
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [access, logout, setCurrentAccountId]);

  return (
    <AuthContext.Provider value={{
      access, user, accounts, currentAccountId, currentAccount,
      isLoading, setTokens, setCurrentAccountId, refreshAccounts, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
